import AddIcon from "@mui/icons-material/Add";
import DownloadIcon from "@mui/icons-material/Download";
import RocketLaunchIcon from "@mui/icons-material/RocketLaunch";
import { Alert, Box, Button, Stack, Tooltip, Typography } from "@mui/material";
import * as React from "react";
import { useEffect, useRef, useState } from "react";
import { useTokenMaker } from "../../../hooks";
import {
  TowingEquipmentAdmin,
  useTowingEquipmentAdmin,
} from "../../../hooks/useTowingEquipmentAdmin";
import { ConfirmationDialog } from "../../../widgets/ConfirmationDialog";
import { EquipmentEditDialog } from "./components/EquipmentEditDialog";
import { EquipmentTable } from "./components/EquipmentTable";

export interface EditFormData {
  name: string;
  width: string;
  length: string;
  height: string;
  tow_bar_length: string;
  no_footprint: boolean;
}

export const TowingEquipmentAdminScreen: React.FC = () => {
  const {
    loading,
    error: hookError,
    towingEquipment,
    createTowingEquipment,
    updateTowingEquipment,
    deleteTowingEquipment,
    refreshTowingEquipment,
  } = useTowingEquipmentAdmin();
  const { url: webURL } = useTokenMaker();

  // Check if we're on production
  const isProduction = window.location.hostname === "app.airplx.com";
  const [selectedEquipment, setSelectedEquipment] =
    useState<TowingEquipmentAdmin | null>(null);
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  const [editingEquipment, setEditingEquipment] =
    useState<TowingEquipmentAdmin | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [componentError, setComponentError] = useState<string | null>(null);
  const [confirmDeploy, setConfirmDeploy] = useState<boolean>(false);
  const [newEquipmentId, setNewEquipmentId] = useState<string | null>(null);
  const tableRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Combine errors
  const error = componentError || hookError;

  // Focus container on mount and select first equipment
  useEffect(() => {
    containerRef.current?.focus();

    // If we have a new equipment ID to select after refresh
    if (newEquipmentId && towingEquipment) {
      const index = towingEquipment.findIndex((e) => e.id === newEquipmentId);
      if (index !== -1) {
        setSelectedEquipment(towingEquipment[index]);
        setSelectedIndex(index);
        setNewEquipmentId(null); // Clear after selection
        return;
      }
    }

    // Default behavior: select first equipment if none selected
    if (towingEquipment && towingEquipment.length > 0 && !selectedEquipment) {
      setSelectedEquipment(towingEquipment[0]);
      setSelectedIndex(0);
    }
  }, [towingEquipment, newEquipmentId]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Don't handle keyboard events when dialog is open
      if (editingEquipment || isCreating) return;
      if (!towingEquipment || towingEquipment.length === 0) return;

      if (e.key === "ArrowDown" || e.key === "ArrowUp") {
        e.preventDefault();
        let newIndex = selectedIndex;

        if (newIndex === null) {
          newIndex = 0;
        } else if (e.key === "ArrowDown") {
          newIndex = Math.min(newIndex + 1, towingEquipment.length - 1);
        } else {
          newIndex = Math.max(newIndex - 1, 0);
        }

        setSelectedIndex(newIndex);
        setSelectedEquipment(towingEquipment[newIndex]);

        // Scroll into view
        if (tableRef.current) {
          const row = tableRef.current.querySelector(
            `tbody tr:nth-child(${newIndex + 1})`
          );
          row?.scrollIntoView({ behavior: "smooth", block: "nearest" });
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [towingEquipment, selectedIndex, editingEquipment, isCreating]);

  const handleSelectEquipment = (
    equipment: TowingEquipmentAdmin,
    index: number
  ) => {
    setSelectedEquipment(equipment);
    setSelectedIndex(index);
  };

  const handleEditClick = (
    equipment: TowingEquipmentAdmin,
    event: React.MouseEvent
  ) => {
    event.stopPropagation();
    setEditingEquipment(equipment);
    setIsCreating(false);
  };

  const handleAddClick = () => {
    setEditingEquipment(null);
    setIsCreating(true);
  };

  const handleDeleteClick = async (
    equipment: TowingEquipmentAdmin,
    event: React.MouseEvent
  ) => {
    event.stopPropagation();
    if (
      window.confirm(`Are you sure you want to delete "${equipment.name}"?`)
    ) {
      try {
        await deleteTowingEquipment(equipment.id);
        if (selectedEquipment?.id === equipment.id) {
          setSelectedEquipment(null);
          setSelectedIndex(null);
        }
      } catch (err) {
        setComponentError(`Failed to delete equipment: ${err}`);
      }
    }
  };

  // Helper function for dimension validation
  const validateDimension = (value: string, allowZero: boolean = false) => {
    if (!value) return null;
    const num = parseFloat(value);
    const isValid =
      !isNaN(num) && (allowZero ? num >= 0 : num > 0) && num <= 1000;
    return isValid ? num : null;
  };

  const handleSaveEdit = async (formData: EditFormData) => {
    try {
      if (isCreating) {
        // Create new equipment
        const width = validateDimension(formData.width);
        const length = validateDimension(formData.length);
        const height = validateDimension(formData.height);
        const towBarLength = formData.tow_bar_length
          ? validateDimension(formData.tow_bar_length, true)
          : null;

        if (!formData.name) {
          setComponentError("Please provide a name for the equipment.");
          return;
        }

        if (!width || !length || !height) {
          setComponentError(
            "Please provide valid values for all dimension fields. Dimensions must be positive numbers up to 1000 feet."
          );
          return;
        }

        const newEquipment = await createTowingEquipment({
          name: formData.name,
          width: width || 0,
          length: length || 0,
          height: height || 0,
          tow_bar_length: towBarLength,
          no_footprint: formData.no_footprint,
        });

        // Check if creation was successful
        if (!newEquipment) {
          setComponentError("Failed to create equipment. Please try again.");
          return;
        }

        // Set the new equipment ID for selection after refresh
        if (typeof newEquipment.id === "string") {
          setNewEquipmentId(newEquipment.id);
        }
        await refreshTowingEquipment();
      } else if (editingEquipment) {
        // Update existing equipment
        const width = formData.width ? validateDimension(formData.width) : null;
        const length = formData.length
          ? validateDimension(formData.length)
          : null;
        const height = formData.height
          ? validateDimension(formData.height)
          : null;
        const towBarLength = formData.tow_bar_length
          ? validateDimension(formData.tow_bar_length, true)
          : undefined;

        // Check if any provided dimensions are invalid
        if (formData.width && !width) {
          setComponentError("Width must be a positive number up to 1000 feet.");
          return;
        }
        if (formData.length && !length) {
          setComponentError(
            "Length must be a positive number up to 1000 feet."
          );
          return;
        }
        if (formData.height && !height) {
          setComponentError(
            "Height must be a positive number up to 1000 feet."
          );
          return;
        }

        const updatedEquipment: TowingEquipmentAdmin = {
          ...editingEquipment,
          name: formData.name || editingEquipment.name,
          width: width ?? editingEquipment.width,
          length: length ?? editingEquipment.length,
          height: height ?? editingEquipment.height,
          tow_bar_length:
            towBarLength !== undefined
              ? towBarLength
              : editingEquipment.tow_bar_length,
          no_footprint: formData.no_footprint,
        };

        await updateTowingEquipment(updatedEquipment);

        // Update selected equipment if it was edited
        const updated = towingEquipment.find(
          (e) => e.id === editingEquipment.id
        );
        if (updated && selectedEquipment?.id === editingEquipment.id) {
          setSelectedEquipment(updated);
        }
      }

      setEditingEquipment(null);
      setIsCreating(false);
      setComponentError(null);
    } catch (err) {
      setComponentError(`Failed to save equipment: ${err}`);
    }
  };

  // Helper to prepare equipment data for export
  const prepareEquipmentExportData = (): {
    headers: string[];
    rows: string[][];
  } => {
    const headers = [
      "Name",
      "Width",
      "Length",
      "Height",
      "Tow Bar Length",
      "ID",
    ];

    const rows = towingEquipment.map((e) => [
      e.name || "",
      e.width?.toFixed(1) || "",
      e.length?.toFixed(1) || "",
      e.height?.toFixed(1) || "",
      e.tow_bar_length?.toFixed(1) || "",
      e.id,
    ]);

    return { headers, rows };
  };

  // Helper to download file
  const downloadFile = (
    content: string,
    filename: string,
    mimeType: string
  ) => {
    const blob = new Blob([content], { type: mimeType });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);

    link.setAttribute("href", url);
    link.setAttribute("download", filename);
    link.style.visibility = "hidden";

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    URL.revokeObjectURL(url);
  };

  const handleExportCSV = () => {
    const { headers, rows } = prepareEquipmentExportData();

    // Convert to TSV format
    const tsvContent = [
      headers.join("\t"),
      ...rows.map((row) => row.join("\t")),
    ].join("\n");

    const filename = `towing_equipment_${
      new Date().toISOString().split("T")[0]
    }.tsv`;
    downloadFile(
      tsvContent,
      filename,
      "text/tab-separated-values;charset=utf-8;"
    );
  };

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography>Loading towing equipment...</Typography>
      </Box>
    );
  }

  if (error && !loading) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" onClose={() => setComponentError(null)}>
          {error}
        </Alert>
      </Box>
    );
  }

  return (
    <Box
      ref={containerRef}
      tabIndex={0}
      sx={{
        p: 2,
        height: "calc(100vh - 120px)",
        display: "flex",
        flexDirection: "column",
        outline: "none",
      }}
    >
      <Stack spacing={3} sx={{ height: "100%" }}>
        <Stack direction="row" justifyContent="flex-end" alignItems="center">
          <Stack direction="row" spacing={2}>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleAddClick}
              color="primary"
            >
              Add Equipment
            </Button>
            <Button
              variant="contained"
              startIcon={<DownloadIcon />}
              onClick={handleExportCSV}
              disabled={towingEquipment.length === 0}
              color="primary"
            >
              Export Equipment List
            </Button>
            <Tooltip
              title={
                !isProduction
                  ? "Deploy is only available on production (app.airplx.com)"
                  : ""
              }
              arrow
            >
              <span>
                <Button
                  onClick={() => setConfirmDeploy(true)}
                  variant="contained"
                  color="primary"
                  startIcon={<RocketLaunchIcon />}
                  disabled={!isProduction}
                >
                  Deploy
                </Button>
              </span>
            </Tooltip>
          </Stack>
        </Stack>

        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            display: "flex",
            flexDirection: "column",
          }}
        >
          <EquipmentTable
            equipment={towingEquipment}
            selectedEquipment={selectedEquipment}
            onSelectEquipment={handleSelectEquipment}
            onEditClick={handleEditClick}
            onDeleteClick={handleDeleteClick}
            tableRef={tableRef}
          />
        </Box>
      </Stack>

      <EquipmentEditDialog
        open={!!editingEquipment || isCreating}
        equipment={editingEquipment}
        isCreating={isCreating}
        onClose={() => {
          setEditingEquipment(null);
          setIsCreating(false);
        }}
        onSave={handleSaveEdit}
      />

      {confirmDeploy && (
        <ConfirmationDialog
          title="Confirm deploy"
          text="Are you sure you want to deploy towing equipment updates?"
          onCancel={() => setConfirmDeploy(false)}
          onClose={() => setConfirmDeploy(false)}
          onOk={async () => {
            await fetch(`${webURL}/jobs/update-towing-equipment`);
            setConfirmDeploy(false);
          }}
          okText="Go baby go!"
        />
      )}
    </Box>
  );
};
